<template>
  <div v-if="show" class="modal fade show" tabindex="-1" style="display: block;" @click.self="close">
    <div class="modal-dialog modal-dialog-centered">
      <div class="modal-content bg-dark text-light">
        <div class="modal-header border-secondary">
          <h5 class="modal-title">
            <i class="fas fa-percentage me-2"></i>
            Aplicar Descuento - {{ brand?.brand_name }}
          </h5>
          <button type="button" class="btn-close btn-close-white" @click="close"></button>
        </div>
        
        <div class="modal-body">
          <form @submit.prevent="applyDiscount">
            <!-- Tipo de descuento -->
            <div class="mb-3">
              <label for="discountType" class="form-label">Tipo de descuento</label>
              <select 
                id="discountType" 
                v-model="formData.discount_type" 
                class="form-select bg-dark text-light border-secondary"
                required
              >
                <option value="">Seleccionar tipo...</option>
                <option value="percentage">Porcentaje (%)</option>
                <option value="fixed">Monto fijo ($)</option>
              </select>
            </div>

            <!-- Valor del descuento -->
            <div class="mb-3">
              <label for="discountValue" class="form-label">
                Valor del descuento
                <span v-if="formData.discount_type === 'percentage'">(%)</span>
                <span v-else-if="formData.discount_type === 'fixed'">($)</span>
              </label>
              <div class="input-group">
                <span v-if="formData.discount_type === 'percentage'" class="input-group-text bg-dark text-light border-secondary">%</span>
                <span v-else-if="formData.discount_type === 'fixed'" class="input-group-text bg-dark text-light border-secondary">$</span>
                <input 
                  id="discountValue"
                  v-model="formData.discount_value" 
                  type="number" 
                  class="form-control bg-dark text-light border-secondary"
                  :placeholder="formData.discount_type === 'percentage' ? 'Ej: 15' : 'Ej: 1000'"
                  :min="0"
                  :max="formData.discount_type === 'percentage' ? 100 : undefined"
                  step="0.01"
                  required
                />
              </div>
              <div class="form-text text-muted">
                <span v-if="formData.discount_type === 'percentage'">
                  El porcentaje debe estar entre 0 y 100%
                </span>
                <span v-else-if="formData.discount_type === 'fixed'">
                  Monto fijo que se descontará del precio
                </span>
              </div>
            </div>

            <!-- Fecha de expiración -->
            <div class="mb-3">
              <label for="expirationDate" class="form-label">
                Fecha de expiración (opcional)
              </label>
              <input 
                id="expirationDate"
                v-model="formData.discount_expires_at" 
                type="date" 
                class="form-control bg-dark text-light border-secondary"
                :min="minDate"
              />
              <div class="form-text text-muted">
                Si no se especifica, el descuento no expirará automáticamente
              </div>
            </div>

            <!-- Vista previa del descuento -->
            <div v-if="formData.discount_type && formData.discount_value > 0" class="alert alert-info">
              <h6 class="mb-2 text-black">
                <i class="fas fa-eye me-2"></i>Vista previa del descuento:
              </h6>
              <div class="d-flex justify-content-between">
                <span>Tipo:</span>
                <span class="fw-bold">
                  {{ formData.discount_type === 'percentage' ? 'Porcentaje' : 'Monto Fijo' }}
                </span>
              </div>
              <div class="d-flex justify-content-between">
                <span>Valor:</span>
                <span class="fw-bold text-primary">
                  {{ formData.discount_type === 'percentage' ? formData.discount_value + '%' : '$' + formData.discount_value }}
                </span>
              </div>
              <div v-if="formData.discount_expires_at" class="d-flex justify-content-between">
                <span>Expira:</span>
                <span class="fw-bold text-warning">
                  {{ new Date(formData.discount_expires_at).toLocaleDateString('es-ES') }}
                </span>
              </div>
              <hr class="my-2">
              <div class="small text-muted">
                <span v-if="formData.discount_type === 'percentage'">
                  Se aplicará un {{ formData.discount_value }}% de descuento a todos los productos de esta marca.
                </span>
                <span v-else>
                  Se descontarán ${{ formData.discount_value }} del precio de todos los productos de esta marca.
                </span>
              </div>
            </div>

            <!-- Información actual de descuento si existe -->
            <div v-if="brand?.discount_active" class="alert alert-warning">
              <h6 class="mb-2 text-black">
                <i class="fas fa-exclamation-triangle me-2"></i>Descuento actual:
              </h6>
              <div class="d-flex justify-content-between">
                <span>Tipo actual:</span>
                <span>{{ brand.discount_type === 'percentage' ? 'Porcentaje' : 'Monto Fijo' }}</span>
              </div>
              <div class="d-flex justify-content-between">
                <span>Valor actual:</span>
                <span class="text-warning">
                  {{ brand.discount_type === 'percentage' ? brand.discount_value + '%' : '$' + brand.discount_value }}
                </span>
              </div>
              <div v-if="brand.discount_expires_at" class="d-flex justify-content-between">
                <span>Expira:</span>
                <span class="text-danger">
                  {{ new Date(brand.discount_expires_at).toLocaleDateString('es-ES') }}
                </span>
              </div>
              <small class="text-muted">El nuevo descuento reemplazará al actual.</small>
            </div>
          </form>
        </div>
        
        <div class="modal-footer border-secondary">
          <button type="button" class="btn btn-secondary" @click="close" :disabled="isLoading">
            Cancelar
          </button>
          <button 
            type="button" 
            class="btn btn-primary" 
            @click="applyDiscount" 
            :disabled="!isFormValid || isLoading"
          >
            <span v-if="isLoading" class="spinner-border spinner-border-sm me-2"></span>
            <i v-else class="fas fa-check me-2"></i>
            {{ isLoading ? 'Aplicando...' : 'Aplicar Descuento' }}
          </button>
          
          <!-- Botón para remover descuento si existe -->
          <button 
            v-if="brand?.discount_active" 
            type="button" 
            class="btn btn-outline-danger" 
            @click="removeDiscount" 
            :disabled="isLoading"
          >
            <i class="fas fa-times me-2"></i>
            Quitar Descuento
          </button>
        </div>
      </div>
    </div>
  </div>
  
  <!-- Backdrop -->
  <div v-if="show" class="modal-backdrop fade show"></div>
</template>

<script setup>
import { ref, computed, watch, defineEmits, defineProps } from 'vue'
import { useToast } from 'vue-toastification'
import { applyBrandDiscount } from '@/services/brands'

const props = defineProps({
  show: {
    type: Boolean,
    default: false
  },
  brand: {
    type: Object,
    default: null
  }
})

const emit = defineEmits(['close', 'success'])

const toast = useToast()
const isLoading = ref(false)

const formData = ref({
  discount_type: '',
  discount_value: 0,
  discount_expires_at: ''
})

// Computed para validar el formulario
const isFormValid = computed(() => {
  return formData.value.discount_type && 
         formData.value.discount_value > 0 &&
         (formData.value.discount_type !== 'percentage' || formData.value.discount_value <= 100)
})

// Computed para la fecha mínima (mañana)
const minDate = computed(() => {
  const tomorrow = new Date()
  tomorrow.setDate(tomorrow.getDate() + 1)
  return tomorrow.toISOString().split('T')[0]
})

// Watch para resetear el formulario cuando se abra el modal
watch(() => props.show, (newValue) => {
  if (newValue && props.brand) {
    // Si la marca ya tiene descuento, prellenar el formulario
    if (props.brand.discount_active) {
      formData.value = {
        discount_type: props.brand.discount_type || '',
        discount_value: props.brand.discount_value || 0,
        discount_expires_at: props.brand.discount_expires_at || ''
      }
    } else {
      // Resetear formulario para nueva marca
      formData.value = {
        discount_type: '',
        discount_value: 0,
        discount_expires_at: ''
      }
    }
  }
})

const close = () => {
  if (!isLoading.value) {
    emit('close')
  }
}

const applyDiscount = async () => {
  if (!isFormValid.value || isLoading.value) {
    return
  }

  try {
    isLoading.value = true
    
    const discountData = {
      discount_type: formData.value.discount_type,
      discount_value: parseFloat(formData.value.discount_value)
    }
    
    // Solo incluir fecha de expiración si se especificó
    if (formData.value.discount_expires_at) {
      discountData.discount_expires_at = formData.value.discount_expires_at
    }
    
    const response = await applyBrandDiscount(props.brand.id, discountData)

    // Mostrar mensaje detallado con información de productos actualizados
    let message = response.message || 'Descuento aplicado exitosamente'
    if (response.products_updated && response.blisters_updated) {
      message = `Descuento aplicado exitosamente a ${response.products_updated} productos (${response.blisters_updated} presentaciones)`
    }
    
    toast.success(message)
    emit('success', response.brand)
    close()
    
  } catch (error) {
    console.error('Error applying discount:', error)
    toast.error(error.message || 'Error al aplicar el descuento')
  } finally {
    isLoading.value = false
  }
}

const removeDiscount = async () => {
  if (!props.brand?.discount_active || isLoading.value) {
    return
  }

  try {
    isLoading.value = true
    
    const response = await applyBrandDiscount(props.brand.id, {
      discount_type: 'percentage',
      discount_value: 0
    })

    // Mostrar mensaje detallado con información de productos actualizados
    let message = 'Descuento removido exitosamente'
    if (response.products_updated && response.blisters_updated) {
      message = `Descuento removido exitosamente de ${response.products_updated} productos (${response.blisters_updated} presentaciones)`
    }
    
    toast.success(message)
    emit('success', response.brand)
    close()
    
  } catch (error) {
    console.error('Error removing discount:', error)
    toast.error(error.message || 'Error al remover el descuento')
  } finally {
    isLoading.value = false
  }
}
</script>

<style scoped>
.modal {
  backdrop-filter: blur(3px);
}

.modal-content {
  border: 1px solid var(--bs-gray-700);
  box-shadow: 0 0.5rem 1rem rgba(0, 0, 0, 0.5);
}

.form-control:focus,
.form-select:focus {
  background-color: var(--bs-dark);
  border-color: var(--bs-primary);
  color: var(--bs-light);
  box-shadow: 0 0 0 0.2rem rgba(var(--bs-primary-rgb), 0.25);
}

.input-group-text {
  border-color: var(--bs-gray-600);
}

.alert {
  border: none;
  border-radius: 0.5rem;
}

.btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.spinner-border-sm {
  width: 1rem;
  height: 1rem;
}
</style>
